import { useEffect, useMemo, useRef } from 'react';
import mapboxgl from 'mapbox-gl';

// Dispatch map — multi-driver day view. Mirrors CNC Delivery's
// DispatchMap.jsx (~/Desktop/cnc-web/src/components/dispatch/DispatchMap.jsx)
// in visual language: dark basemap, per-driver colored pins,
// left driver-legend overlay, top-center stats HUD.
//
// Phase 1 scope (this file): colors + legend + stats HUD only.
// Phase 2+ (deferred): pharmacy filter tabs, cold-chain toggle, style
// switcher (Dark/Light/Streets/Satellite/Nav), labeled origin pins
// (H/A), click-popup with move-to dropdown, batch select
// (shift-drag / shift-click), search with highlight+zoom, geocoding.
//
// Sister component: apps/web/src/components/routes/RouteMap.tsx
// (single-route detail view). The two are intentionally separate —
// dispatch's multi-driver / filter / batch surface is different enough
// that overloading one component would hurt both.

// CNC's driver palette. 20 stops — after that we wrap. Copied verbatim
// from DispatchMap.jsx line 17-22 for visual parity.
const DRIVER_COLORS = [
  '#2563eb', '#dc2626', '#16a34a', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316', '#6366f1', '#14b8a6',
  '#e11d48', '#84cc16', '#0ea5e9', '#a855f7', '#ef4444',
  '#22c55e', '#eab308', '#3b82f6', '#d946ef', '#64748b',
] as const;

// Dark basemap default matches the screenshot Dom shared. Streets is
// available via the Phase 2 style switcher.
const MAPBOX_STYLE_URL = 'mapbox://styles/mapbox/dark-v11';
// Only used when VITE_MAPBOX_ACCESS_TOKEN is unset in dev. See
// RouteMap.tsx for the same fallback rationale.
const DEMO_STYLE_URL = 'https://demotiles.maplibre.org/style.json';

function resolveStyleUrl(): string {
  const key = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
  if (!key) {
    // eslint-disable-next-line no-console
    console.warn(
      '[DispatchMap] VITE_MAPBOX_ACCESS_TOKEN not set; using low-fidelity demo tiles',
    );
    mapboxgl.accessToken = '';
    return DEMO_STYLE_URL;
  }
  mapboxgl.accessToken = key;
  return MAPBOX_STYLE_URL;
}

// Framing constants mirror RouteMap's — see the sibling for rationale.
const SOLO_STOP_ZOOM = 14;
const FIT_PADDING = 40;
const FIT_MAX_ZOOM = 12;

// Shape passed in from the dispatch route flattener. String/number
// lat/lng both accepted — PostgREST returns numerics as strings.
export type DispatchMapStop = {
  id: string;
  latitude: string | number | null;
  longitude: string | number | null;
  address: string | null;
  driver_id: string | null;
  driver_name: string; // "Unassigned" fallback when driver is null
  is_cold_chain: boolean;
};

type Geocoded = {
  id: string;
  lat: number;
  lng: number;
  driver_name: string;
  is_cold_chain: boolean;
};

function parseStops(stops: DispatchMapStop[]): Geocoded[] {
  const out: Geocoded[] = [];
  for (const s of stops) {
    if (s.latitude === null || s.longitude === null) continue;
    const lat = typeof s.latitude === 'string' ? Number(s.latitude) : s.latitude;
    const lng = typeof s.longitude === 'string' ? Number(s.longitude) : s.longitude;
    if (Number.isNaN(lat) || Number.isNaN(lng)) continue;
    out.push({
      id: s.id,
      lat,
      lng,
      driver_name: s.driver_name,
      is_cold_chain: s.is_cold_chain,
    });
  }
  return out;
}

// Build a stable driver → color mapping. Driver names sorted
// alphabetically, then each gets the palette entry at their index
// (modulo palette size). Matches CNC's DispatchMap.jsx:145-151.
// Exported for testing.
export function buildColorMap(driverNames: string[]): Record<string, string> {
  const sorted = [...new Set(driverNames)].sort();
  const m: Record<string, string> = {};
  sorted.forEach((name, i) => {
    m[name] = DRIVER_COLORS[i % DRIVER_COLORS.length]!;
  });
  return m;
}

// Per-driver stop count aggregation. Exported for testing + legend
// rendering. Preserves the sort order returned by buildColorMap.
export function driverSummary(
  stops: DispatchMapStop[],
): Array<{ name: string; color: string; count: number }> {
  const counts = new Map<string, number>();
  for (const s of stops) {
    counts.set(s.driver_name, (counts.get(s.driver_name) ?? 0) + 1);
  }
  const colorMap = buildColorMap(Array.from(counts.keys()));
  return Object.keys(colorMap).map((name) => ({
    name,
    color: colorMap[name]!,
    count: counts.get(name) ?? 0,
  }));
}

function coldCount(stops: DispatchMapStop[]): number {
  let n = 0;
  for (const s of stops) if (s.is_cold_chain) n += 1;
  return n;
}

// First-name only for the legend — the pin is the identifier, first
// name is the label. Matches CNC's convention.
function firstName(full: string): string {
  return full.split(' ')[0] ?? full;
}

// Inline marker CSS. Kept as a factory so the color parameter varies
// per-driver without allocating a class per color.
function markerCss(color: string): string {
  return [
    'width:13px',
    'height:13px',
    'border-radius:50%',
    `background:${color}`,
    'border:2px solid rgba(255,255,255,0.9)',
    'box-shadow:0 0 0 1px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.25)',
    'cursor:default', // Phase 2 will add click → popup; default cursor for now.
  ].join(';');
}

export type DispatchMapProps = {
  stops: DispatchMapStop[];
  className?: string;
};

export function DispatchMap({ stops, className }: DispatchMapProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);

  // Derived once per stops change — passed to the legend + used to
  // resolve marker colors during plotting.
  const summary = useMemo(() => driverSummary(stops), [stops]);
  const stopsCount = stops.length;
  const cold = useMemo(() => coldCount(stops), [stops]);
  const driversCount = summary.length;

  // Build + tear down the map on mount / stops change. Following
  // RouteMap's pattern — everything happens in one effect.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const map = new mapboxgl.Map({
      container,
      style: resolveStyleUrl(),
      center: [-81.52, 41.08], // Akron, OH — CNC's operating region.
      zoom: 10,
    });
    mapRef.current = map;
    // Built-in nav (zoom + no compass). Phase 2 will add home button
    // + style switcher.
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

    const placeMarkers = (): void => {
      const geocoded = parseStops(stops);
      const colorMap = buildColorMap(geocoded.map((s) => s.driver_name));

      for (const s of geocoded) {
        const color = colorMap[s.driver_name] ?? '#64748b';
        const el = document.createElement('div');
        el.setAttribute('data-stop-id', s.id);
        el.setAttribute('data-driver-name', s.driver_name);
        el.style.cssText = markerCss(color);
        const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
          .setLngLat([s.lng, s.lat])
          .addTo(map);
        markersRef.current.push(marker);
      }

      // Frame to fit all pins. Solo / all-same-coord fall back to
      // setCenter+setZoom the same way RouteMap does.
      const first = geocoded[0];
      if (!first) return;
      const allSame = geocoded.every((s) => s.lat === first.lat && s.lng === first.lng);
      if (geocoded.length === 1 || allSame) {
        map.setCenter([first.lng, first.lat]);
        map.setZoom(SOLO_STOP_ZOOM);
        return;
      }
      const bounds = new mapboxgl.LngLatBounds();
      for (const s of geocoded) bounds.extend([s.lng, s.lat]);
      map.fitBounds(bounds, {
        padding: FIT_PADDING,
        maxZoom: FIT_MAX_ZOOM,
        duration: 0,
      });
    };

    map.on('load', placeMarkers);

    return () => {
      for (const m of markersRef.current) m.remove();
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, [stops]);

  return (
    <div
      className={`relative ${className ?? 'h-full w-full'}`}
      data-testid="dispatch-map"
      aria-label="Dispatch map of all day's stops"
      role="region"
    >
      <div ref={containerRef} className="h-full w-full" />

      {/* Left driver-legend overlay. Absolute-positioned glass panel
          over the map. Scrolls internally if driver count exceeds
          panel height — the panel is capped so it never overwhelms
          the map on tall viewports. */}
      <div
        data-testid="dispatch-map-legend"
        className="absolute left-3 top-3 z-10 w-52 max-h-[calc(100%-1.5rem)] overflow-y-auto rounded-lg bg-white/95 shadow-lg ring-1 ring-black/5 backdrop-blur px-2 py-2"
      >
        <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-ink-3">
          Drivers ({driversCount})
        </div>
        <ul className="space-y-0.5">
          {summary.map((d) => (
            <li
              key={d.name}
              className="flex items-center gap-2 rounded-md px-2 py-1 text-xs text-ink"
              data-driver-name={d.name}
            >
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ background: d.color }}
                aria-hidden
              />
              <span className="flex-1 truncate font-medium">{firstName(d.name)}</span>
              <span className="text-[11px] font-medium text-ink-3">{d.count}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Top-center stats HUD. Dark glass panel with three stat
          columns — mirrors CNC's DispatchMap.jsx:654-674. */}
      <div
        data-testid="dispatch-map-hud"
        className="pointer-events-none absolute left-1/2 top-3 z-10 flex -translate-x-1/2 overflow-hidden rounded-lg bg-black/65 shadow-lg backdrop-blur"
      >
        <div className="border-r border-white/10 px-4 py-2 text-center">
          <div className="text-sm font-bold leading-none text-white">{stopsCount}</div>
          <div className="mt-1 text-[9px] font-medium tracking-wider text-white/45">STOPS</div>
        </div>
        <div className="border-r border-white/10 px-4 py-2 text-center">
          <div className="text-sm font-bold leading-none text-sky-400">{cold}</div>
          <div className="mt-1 text-[9px] font-medium tracking-wider text-white/45">COLD</div>
        </div>
        <div className="px-4 py-2 text-center">
          <div className="text-sm font-bold leading-none text-white">{driversCount}</div>
          <div className="mt-1 text-[9px] font-medium tracking-wider text-white/45">DRIVERS</div>
        </div>
      </div>
    </div>
  );
}
